import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Clock, AlertTriangle, CheckCircle, Zap } from "lucide-react";
import api from "../api/client";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

function Performance() {
  const [metrics, setMetrics] = useState([]);
  const [summary, setSummary] = useState({});
  const [timeRange, setTimeRange] = useState("24h");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, [timeRange]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/v1/performance/api?time_range=${timeRange}`,
      );
      setMetrics(response.data.metrics);
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  // Chart data
  const chartData = {
    labels: metrics.map((m) => new Date(m.timestamp).toLocaleTimeString()),
    datasets: [
      {
        label: "Response Time (ms)",
        data: metrics.map((m) => m.duration),
        borderColor: "rgb(6, 182, 212)",
        backgroundColor: "rgba(6, 182, 212, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: "#9CA3AF" },
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.9)",
        borderColor: "rgba(55, 65, 81, 0.5)",
        borderWidth: 1,
        titleColor: "#F3F4F6",
        bodyColor: "#D1D5DB",
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(255,255,255,0.05)" },
        ticks: { color: "#9CA3AF" },
      },
      y: {
        grid: { color: "rgba(255,255,255,0.05)" },
        ticks: { color: "#9CA3AF" },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-white">
          Performance Monitoring
        </h1>
        <div className="flex gap-2">
          {["1h", "6h", "24h", "7d"].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                timeRange === range
                  ? "bg-cyan-500 text-white"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-700"
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Zap className="w-4 h-4" />
            <span>Requests</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total || 0}</p>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Clock className="w-4 h-4" />
            <span>Avg Response</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {summary.avg_duration
              ? `${Math.round(summary.avg_duration)}ms`
              : "N/A"}
          </p>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span>Slow Requests</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">
            {summary.slow_count || 0}
          </p>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <CheckCircle className="w-4 h-4 text-red-400" />
            <span>Errors</span>
          </div>
          <p className="text-2xl font-bold text-red-400">
            {summary.error_count || 0}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
        <h3 className="text-sm font-medium text-gray-400 mb-4">
          Response Times
        </h3>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
          </div>
        ) : metrics.length > 0 ? (
          <div className="h-72">
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            No performance data yet. Start using your app to see metrics.
          </div>
        )}
      </div>

      {/* Recent Metrics Table */}
      <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
        <h3 className="text-sm font-medium text-gray-400 mb-4">
          Recent Requests
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800">
                <th className="text-left py-2">Method</th>
                <th className="text-left py-2">URL</th>
                <th className="text-right py-2">Duration</th>
                <th className="text-right py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {metrics.slice(0, 20).map((m) => (
                <tr key={m.id} className="border-b border-gray-800/50">
                  <td className="py-2 text-gray-300">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        m.method === "GET"
                          ? "bg-blue-500/20 text-blue-400"
                          : m.method === "POST"
                            ? "bg-green-500/20 text-green-400"
                            : m.method === "PUT"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : m.method === "DELETE"
                                ? "bg-red-500/20 text-red-400"
                                : "bg-gray-500/20 text-gray-400"
                      }`}
                    >
                      {m.method}
                    </span>
                  </td>
                  <td className="py-2 text-gray-300 truncate max-w-xs">
                    {m.url}
                  </td>
                  <td
                    className={`py-2 text-right ${m.duration > 1000 ? "text-yellow-400" : "text-green-400"}`}
                  >
                    {Math.round(m.duration)}ms
                  </td>
                  <td
                    className={`py-2 text-right ${m.status >= 400 ? "text-red-400" : "text-green-400"}`}
                  >
                    {m.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Performance;
